

import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { LogEvent } from '../peregrineFlowInterfaces';
import { socket } from '../socket';
import { Alert, Box, Divider, List, ListItem, ListSubheader, Typography, Badge, Tooltip, IconButton } from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteIcon from '@mui/icons-material/Delete';

import './logs-pane.css';


interface Indexable {
    [key: string]: any;
}
const levelColorMapping = {
    'DEBUG': 'info',
    'INFO': 'success',
    'WARNING': 'warning',
    'ERROR': 'error'
} as Indexable

interface LogsPaneProps {
    expanded?: boolean;
    onToggleExpand?: () => void;
}

function LogsPane({ expanded = true, onToggleExpand }: LogsPaneProps) {
    const maxLogEntries = 500; // can be a prop in the future
    const [logs, setLogs] = useState<Array<LogEvent & { id: string }>>([])
    const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set(Object.keys(levelColorMapping)));
    const logsBuffer = useRef<Array<LogEvent>>([]);
    const flushTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const logsEndRef = useRef<HTMLDivElement>(null);

    const toggleFilter = (level: string) => {
        if (!expanded) {
            setActiveFilters(new Set([level]));
            if (onToggleExpand) {
                onToggleExpand();
            }
        } else {
            setActiveFilters(prev => {
                const newFilters = new Set(prev);
                if (newFilters.has(level)) {
                    newFilters.delete(level);
                } else {
                    newFilters.add(level);
                }
                return newFilters;
            });
        }
    };

    const flushLogs = useCallback(() => {
        if (logsBuffer.current.length === 0) return;

        const newLogs = [...logsBuffer.current];
        logsBuffer.current = [];
        flushTimeout.current = null;

        setLogs(currentLogs => {
            const newLogsWithIds = newLogs.map(log => ({
                ...log,
                id: Math.random().toString(36).substr(2, 9)
            })); // .reverse() removed to append at bottom

            const updatedLogs = [...currentLogs, ...newLogsWithIds];
            if (updatedLogs.length > maxLogEntries) {
                return updatedLogs.slice(updatedLogs.length - maxLogEntries);
            }
            return updatedLogs;
        });
    }, []);

    useEffect(() => {
        if (expanded && logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs, expanded]);

    useEffect(() => {
        const onLogEvent = (log: LogEvent) => {
            logsBuffer.current.push(log);
            if (!flushTimeout.current) {
                flushTimeout.current = setTimeout(flushLogs, 100);
            }
        };

        socket.on('log', onLogEvent);
        return () => {
            socket.off('log', onLogEvent);
            if (flushTimeout.current) {
                clearTimeout(flushTimeout.current);
            }
        };
    }, [flushLogs]);

    const counts = useMemo(() => {
        return logs.reduce((acc, log) => {
            acc[log.level] = (acc[log.level] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);
    }, [logs]);

    const renderLogs = () => {
        return logs
            .filter(log => activeFilters.has(log.level))
            .map((log) => {
                const key = log.id
                const color = levelColorMapping[log.level]

                return (
                    <ListItem key={key} sx={{ py: 0.5, px: 2 }}>
                        <Alert severity={color} sx={{ width: '100%', py: 0 }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {log.message}
                            </Typography>
                        </Alert>
                    </ListItem>
                )
            })
    }

    return (
        <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}} className='logs-pane'>
            {expanded && (
                <Box sx={{ flexGrow: 1, overflow: 'auto', padding: 0 }}>
                    <List
                        sx={{ bgcolor: 'background.paper', padding: 0 }}
                        key='log-panel-head'
                        subheader={
                            <ListSubheader sx={{ lineHeight: '30px' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2">Logs</Typography>
                                    <IconButton size="small" onClick={() => setLogs([])}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Divider />
                            </ListSubheader>
                        }>
                        {renderLogs()}
                        <div ref={logsEndRef} />
                    </List>
                </Box>
            )}
            <Box sx={{
                height: '40px',
                minHeight: '40px',
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                display: 'flex',
                alignItems: 'center',
                px: 2,
                bgcolor: 'background.paper',
                gap: 2
            }}  className="bar">
                {onToggleExpand && (
                    <IconButton onClick={onToggleExpand} size="small" color='inherit'>
                        {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                    </IconButton>
                )}
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <Tooltip title="Debug">
                        <Box
                            onClick={() => toggleFilter('DEBUG')}
                            sx={{
                                cursor: 'pointer',
                                opacity: activeFilters.has('DEBUG') ? 1 : 0.3,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Badge badgeContent={counts['DEBUG'] || 0} color="info" showZero>
                                <BugReportIcon fontSize="small" color="inherit" />
                            </Badge>
                        </Box>
                    </Tooltip>
                    <Tooltip title="Info">
                        <Box
                            onClick={() => toggleFilter('INFO')}
                            sx={{
                                cursor: 'pointer',
                                opacity: activeFilters.has('INFO') ? 1 : 0.3,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Badge badgeContent={counts['INFO'] || 0} color="success" showZero>
                                <InfoIcon fontSize="small" color="inherit" />
                            </Badge>
                        </Box>
                    </Tooltip>
                    <Tooltip title="Warning">
                        <Box
                            onClick={() => toggleFilter('WARNING')}
                            sx={{
                                cursor: 'pointer',
                                opacity: activeFilters.has('WARNING') ? 1 : 0.3,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Badge badgeContent={counts['WARNING'] || 0} color="warning" showZero>
                                <WarningIcon fontSize="small" color="inherit" />
                            </Badge>
                        </Box>
                    </Tooltip>
                    <Tooltip title="Error">
                        <Box
                            onClick={() => toggleFilter('ERROR')}
                            sx={{
                                cursor: 'pointer',
                                opacity: activeFilters.has('ERROR') ? 1 : 0.3,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Badge badgeContent={counts['ERROR'] || 0} color="error" showZero>
                                <ErrorIcon fontSize="small" color="inherit" />
                            </Badge>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>
        </div>
    );
}

export default LogsPane;
